import type { Tier } from '@shared/hooks/useBreakpoint';
import { IndexState, StatusChip } from './ui';
import { daysSince, isStale, reviewDate, type ArticleRow } from './types';

// 12a, re-cut as artboard 16a (phase 27) — the register for the data this
// deployment actually has. No migration, no new columns, no new writes.
//
// Two fields that were being wasted now carry the screen:
//
//   LAST REVIEWED — `18 Aug 2026` right-aligned is metadata. The same value
//   with its age beside it is an editorial signal, and it is the default sort,
//   so the header's arrow means something.
//
//   VIEWS — with helpfulness gone this is the only number saying anyone read
//   the article, so it gets full ink at 13px rather than sitting at metadata
//   weight. Stale with views is urgent; stale without is a candidate for
//   archiving. That is the second axis.
//
// There is deliberately no HELPFULNESS column: nothing collects ratings, and a
// column reading `no ratings` on every row teaches people to skip that region.

const MONO = 'font-mono';

const W = {
  check: 38,
  status: 104,
  views: 88,
  reviewed: 158,
  indexed: 132,
  menu: 38,
} as const;

/** SEARCH INDEX drops first, then STATUS moves under the title.
 *  VIEWS and LAST REVIEWED never drop — they are the screen. */
function columnsFor(tier: Tier) {
  return {
    indexed: tier === 'full' || tier === 'laptop',
    status: tier !== 'tabletP',
  };
}

/**
 * Least recently reviewed first — the default sort IS the screen's opinion
 * about what needs attention, the same argument as Reports' `OPEN HELD ↓`.
 */
export function byLeastRecentlyReviewed(a: ArticleRow, b: ArticleRow): number {
  return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
}

/**
 * The review band, computed from `updatedAt` alone.
 *
 * "Nothing here is wrong yet — but nobody has checked" is the true claim.
 * Staleness is not error, and overstating it turns the band into noise inside
 * a month. Absent when nothing is stale — never rendered saying zero.
 */
export function ReviewBand({
  stale,
  onReview,
}: {
  stale: ArticleRow[];
  onReview: (id: string) => void;
}) {
  if (stale.length === 0) return null;
  const oldest = stale[0];
  const days = daysSince(oldest.updatedAt);

  return (
    <div className="flex flex-wrap items-center gap-[14px] bg-[var(--co-ink)] px-[28px] py-[9px]">
      <span
        className={`${MONO} flex-none text-[9.5px] font-semibold tracking-[.2em] text-[var(--co-brass-pale)]`}
      >
        {`${stale.length} ARTICLE${stale.length === 1 ? '' : 'S'} UNREVIEWED FOR OVER 60 DAYS`}
      </span>
      <span
        className="min-w-0 flex-1 text-[12px] text-[var(--co-on-ink-2)]"
        style={{ textWrap: 'pretty' } as React.CSSProperties}
      >
        {stale.length === 1
          ? `“${oldest.title}” was last touched ${days} days ago and has been read ${oldest.views} ${
              oldest.views === 1 ? 'time' : 'times'
            } since. Nothing here is wrong yet — but nobody has checked.`
          : `The oldest was last touched ${days} days ago. Nothing here is wrong yet — but nobody has checked.`}
      </span>
      <button
        type="button"
        onClick={() => onReview(oldest.id)}
        className={`${MONO} flex-none whitespace-nowrap text-[10px] font-medium tracking-[.13em] text-[var(--co-brass-pale)] hover:underline`}
      >
        {`REVIEW ${stale.length === 1 ? 'IT' : 'THEM'} →`}
      </button>
    </div>
  );
}

function ColumnHead({
  children,
  width,
  align = 'left',
  className = '',
  emphasis,
}: {
  children?: React.ReactNode;
  width?: number;
  align?: 'left' | 'right';
  className?: string;
  emphasis?: boolean;
}) {
  return (
    <div
      role="columnheader"
      className={`${MONO} ${width ? 'flex-none' : 'min-w-0 flex-1'} text-[9.5px] font-semibold tracking-[.2em] ${
        align === 'right' ? 'text-right' : ''
      } ${className}`}
      style={{
        width,
        // The sorted column's label is full ink, so the arrow reads as a fact
        // about the order rather than decoration.
        color: emphasis ? 'var(--co-ink)' : 'var(--co-ink-4)',
      }}
    >
      {children}
    </div>
  );
}

export function RegisterHead({
  tier,
  allSelected,
  onSelectAllOnPage,
}: {
  tier: Tier;
  allSelected: boolean;
  onSelectAllOnPage: (checked: boolean) => void;
}) {
  const cols = columnsFor(tier);
  return (
    <div
      role="row"
      className="mt-[14px] flex items-center border-b border-[var(--co-line)] pb-[11px]"
      style={{ borderLeft: '2px solid transparent' }}
    >
      <div className="flex flex-none items-center justify-center" style={{ width: W.check }}>
        <input
          type="checkbox"
          checked={allSelected}
          onChange={(e) => onSelectAllOnPage(e.target.checked)}
          aria-label="Select every article on this page"
          className="h-[13px] w-[13px] border border-[var(--co-line-strong)] accent-[var(--co-ink)]"
        />
      </div>
      <ColumnHead className="pr-4">ARTICLE</ColumnHead>
      {cols.status && <ColumnHead width={W.status}>STATUS</ColumnHead>}
      <ColumnHead width={W.views} align="right" className="pr-5">
        VIEWS
      </ColumnHead>
      <ColumnHead width={W.reviewed} emphasis>
        LAST REVIEWED ↑
      </ColumnHead>
      {cols.indexed && <ColumnHead width={W.indexed}>SEARCH INDEX</ColumnHead>}
      <ColumnHead width={W.menu} />
    </div>
  );
}

export function ArticleRegisterRow({
  row,
  tier,
  selected,
  onToggle,
  onOpen,
  onMenu,
}: {
  row: ArticleRow;
  tier: Tier;
  selected: boolean;
  onToggle: (checked: boolean) => void;
  onOpen: () => void;
  onMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const cols = columnsFor(tier);
  const days = daysSince(row.updatedAt);
  const stale = isStale(row.updatedAt);

  return (
    <div
      role="row"
      className="flex items-center border-b border-[var(--co-line-subtle)] py-[11px]"
      style={{
        borderLeft: `2px solid ${selected ? 'var(--co-ink)' : 'transparent'}`,
        background: selected ? 'var(--co-raised)' : undefined,
      }}
    >
      <div className="flex flex-none items-center justify-center" style={{ width: W.check }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={(e) => onToggle(e.target.checked)}
          aria-label={`Select ${row.title}`}
          className="h-[13px] w-[13px] border border-[var(--co-line-strong)] accent-[var(--co-ink)]"
        />
      </div>

      <div className="min-w-0 flex-1 pr-4">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onOpen}
            className="min-w-0 truncate text-left text-[13.5px] font-semibold text-[var(--co-ink)] hover:underline"
          >
            {row.title}
          </button>
          {!cols.status && <StatusChip status={row.status} />}
        </div>
        {/* The author is who to ask about an article; the category is where the
            gap lives. Both are already on the record. */}
        <div className={`${MONO} mt-1 truncate text-[11px] text-[var(--co-ink-4)]`}>
          {[`/${row.slug}`, (row.category ?? 'uncategorised').toUpperCase(), row.author?.toUpperCase()]
            .filter(Boolean)
            .join(' · ')}
        </div>
      </div>

      {cols.status && (
        <div className="flex-none" style={{ width: W.status }}>
          <StatusChip status={row.status} />
        </div>
      )}

      {/* Full ink at 13px — heavier than the metadata around it, on purpose. */}
      <div
        className={`${MONO} flex-none pr-5 text-right text-[13px] font-medium tabular-nums text-[var(--co-ink)]`}
        style={{ width: W.views }}
      >
        {row.views.toLocaleString()}
      </div>

      <div className="flex-none" style={{ width: W.reviewed }}>
        <div className={`${MONO} text-[12px] font-medium tabular-nums text-[var(--co-ink-2)]`}>
          {reviewDate(row.updatedAt)}
        </div>
        <div
          className={`${MONO} mt-[5px] text-[9.5px] font-semibold tracking-[.08em]`}
          style={{ color: stale ? 'var(--co-terracotta)' : 'var(--co-ink-4)' }}
        >
          {`${days} ${days === 1 ? 'DAY' : 'DAYS'} AGO`}
        </div>
      </div>

      {cols.indexed && (
        <div className="flex-none" style={{ width: W.indexed }}>
          <IndexState indexed={row.isIndexed} />
        </div>
      )}

      <div className="flex flex-none justify-center" style={{ width: W.menu }}>
        <button
          type="button"
          onClick={onMenu}
          aria-label={`Actions for ${row.title}`}
          className={`${MONO} text-[13px] text-[var(--co-line-strong)] hover:text-[var(--co-ink)]`}
        >
          ⋯
        </button>
      </div>
    </div>
  );
}

/**
 * The foot: what is shown, and what is missing.
 *
 * The second sentence costs nothing where it sits and tells a returning reader
 * why the screen is simpler than the design they signed off — it makes the
 * backend work legible as a choice rather than an omission, so when ratings do
 * land the column arrives as a promise kept.
 */
export function RegisterFoot({ shown, total }: { shown: number; total: number }) {
  return (
    <div className="flex flex-wrap items-center gap-[14px] border-t border-[var(--co-line-frame)] px-[28px] py-3">
      <span className={`${MONO} text-[10.5px] tracking-[.1em] text-[var(--co-ink-4)]`}>
        {shown === total ? `ALL ${total} ARTICLES SHOWN` : `SHOWING ${shown} OF ${total}`}
      </span>
      <div className="flex-1" />
      <span className={`${MONO} text-[11.5px] leading-[1.5] tracking-[.06em] text-[var(--co-ink-3)]`}>
        Helpfulness and citation counts appear here once ratings and AI citations are recorded.
      </span>
    </div>
  );
}
